// frontend/src/App.js
import React, { useState, useEffect } from 'react';
import { Loader2, Zap, Share2, Copy } from 'lucide-react';
import {
  TwitterShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  TwitterIcon,
  FacebookIcon,
  LinkedinIcon,
  WhatsappIcon,
} from 'react-share';

import AdComponent from './components/AdComponent';
import ContactInfo from './components/ContactInfo';
import WordCloud from './components/WordCloud';
import { config } from './config/config';
import './styles.css';

const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:3001/api' : '/api';
const BASE_REQUEST_TIMEOUT = 45000;

const App = () => {
  const [profession, setProfession] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAbout, setShowAbout] = useState(false);
  const [aboutContent, setAboutContent] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [expandedExplanations, setExpandedExplanations] = useState({});
  // Phase-2 narration: Jev's score renders instantly; these hold the real
  // prose paragraphs as they stream in afterward, keyed '1y'/'5y'/'10y'.
  const [narratedText, setNarratedText] = useState({});
  const [narrating, setNarrating] = useState({});
  const narrateAbortRef = React.useRef(null);

  useEffect(() => {
    if (showAbout && !aboutContent) {
      fetch('/content/about.html')
        .then(response => {
          if (!response.ok) throw new Error('Network response was not ok while fetching about.html');
          return response.text();
        })
        .then(html => setAboutContent(html))
        .catch(err => {
          console.error('Error loading about content:', err);
          setAboutContent('<p>Failed to load about content. Please try again later.</p>');
        });
    }
  }, [showAbout, aboutContent]);

  const startNarration = async (forProfession, scores) => {
    if (narrateAbortRef.current) narrateAbortRef.current.abort();
    const controller = new AbortController();
    narrateAbortRef.current = controller;
    setNarratedText({});
    setNarrating({ '1y': true, '5y': true, '10y': true });
    try {
      const response = await fetch(`${API_URL}/narrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profession: forProfession, scores }),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) return;
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done: streamDone } = await reader.read();
        if (streamDone) break;
        buf += decoder.decode(value, { stream: true });
        const blocks = buf.split('\n\n');
        buf = blocks.pop();
        for (const block of blocks) {
          const lines = block.split('\n');
          const evLine = lines.find((l) => l.startsWith('event:'));
          const dataLine = lines.find((l) => l.startsWith('data:'));
          if (!evLine || !dataLine) continue;
          const ev = evLine.replace('event:', '').trim();
          let payload;
          try { payload = JSON.parse(dataLine.replace('data:', '').trim()); } catch { continue; }
          if (ev === 'token') {
            setNarratedText((prev) => ({ ...prev, [payload.horizon]: (prev[payload.horizon] || '') + payload.delta }));
          } else if (ev === 'done' || ev === 'failed') {
            setNarrating((prev) => ({ ...prev, [payload.horizon]: false }));
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.warn('Narration stream error:', err.message);
    } finally {
      setNarrating({ '1y': false, '5y': false, '10y': false });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!profession.trim()) { setError("Please enter a profession."); return; }
    setLoading(true); setError(null); setShowAbout(false); setResults(null);
    setLinkCopied(false);
    setExpandedExplanations({}); // Reset explanations on new submit
    if (narrateAbortRef.current) narrateAbortRef.current.abort();
    setNarratedText({}); setNarrating({});

    const currentRequestTimeout = BASE_REQUEST_TIMEOUT;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), currentRequestTimeout);

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profession: profession.trim()
        }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Request failed with status: ${response.status}`);
      if (!data.success || !data.scores) throw new Error(data.error || 'Received an invalid response from the server.');
      setResults({ ...data, profession: profession.trim() });
      if (data.narrationAvailable && data.scores) startNarration(profession.trim(), data.scores);
    } catch (err) {
      console.error('API Error:', err);
      if (err.name === 'AbortError') setError('The analysis request timed out. This could be due to high server load or network issues. Please try again.');
      else setError(`Failed to get assessment: ${err.message}`);
      setResults(null);
    } finally { setLoading(false); }
  };

  const getRiskColor = (score) => {
    if (typeof score !== 'number' || isNaN(score)) return 'gray';
    if (score >= 70) return 'red';
    if (score >= 50) return 'yellow';
    if (score >= 30) return 'amber';
    return 'green';
  };

  const startNewAssessment = () => {
    setResults(null); setProfession(''); setError(null); setShowAbout(false);
    setLinkCopied(false);
    setExpandedExplanations({}); // Reset explanations
    if (narrateAbortRef.current) narrateAbortRef.current.abort();
    setNarratedText({}); setNarrating({});
  };

  const handleCopyLink = (messageToCopy) => {
    navigator.clipboard.writeText(messageToCopy).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    }).catch(err => {
      console.error('Failed to copy link using navigator.clipboard: ', err);
      try {
          const textArea = document.createElement("textarea");
          textArea.value = messageToCopy;
          textArea.style.position = "fixed"; textArea.style.top = "0"; textArea.style.left = "0";
          document.body.appendChild(textArea);
          textArea.focus(); textArea.select();
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          if (successful) {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2500);
          } else { console.error('Fallback copy method (execCommand) failed.'); alert('Failed to copy link automatically. Please copy it manually.');}
      } catch (fallbackErr) {
          console.error('Fallback copy method failed entirely:', fallbackErr);
          alert('Failed to copy link automatically. Please copy it manually.');
      }
    });
  };

  const toggleExplanation = (periodKey) => {
    setExpandedExplanations(prev => ({
      ...prev,
      [periodKey]: !prev[periodKey]
    }));
  };

  const renderResultsContent = () => {
    if (showAbout) {
      return (
        <div className="w-full max-w-2xl mx-auto my-8 px-4">
          <div className="bg-white rounded-lg shadow-xl p-6 sm:p-10">
            <button onClick={() => setShowAbout(false)} className="text-blue-600 hover:text-blue-800 mb-6 text-sm font-medium">← Back to analyzer</button>
            {aboutContent ? <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: aboutContent }}></div> : <div className="flex justify-center items-center p-10"><Loader2 className="h-12 w-12 text-blue-500 animate-spin" /><p className="ml-4 text-gray-600">Loading content...</p></div>}
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="w-full max-w-lg mx-auto my-12 px-4">
          <div className="bg-white rounded-lg shadow-xl p-8 text-center">
            <div className="flex justify-center mb-6"><Loader2 className="h-16 w-16 text-blue-600 animate-spin" /></div>
            <p className="text-gray-700 text-xl font-semibold mb-3 animate-pulse">
              Analyzing: <span className="font-bold text-blue-600">"{profession.trim()}"</span>
            </p>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden my-6"><div className="bg-blue-500 h-3 rounded-full animate-progress-indeterminate"></div></div>
            <p className="text-sm text-gray-500">This may take a bit longer, thank you for your patience.</p>
          </div>
        </div>
      );
    }

    if (results) {
      const shareUrl = "https://ai-vs.me";
      const highestRiskScore = Math.max(results.scores['1_year'] || 0, results.scores['5_year'] || 0, results.scores['10_year'] || 0);
      const hashtags = ["AIvsMe", "AIJobRisk", "FutureOfWork", "Automation"];
      const generalShareMessage = `I analyzed "${results.profession}" for AI risk on ai-vs.me! 🤖 Max risk: ${highestRiskScore}%. Is your job safe? Check yours: ${shareUrl}`;
      const facebookShareQuote = `I analyzed "${results.profession}" for AI risk on ai-vs.me! Max risk: ${highestRiskScore}%. Is your job safe? Check yours out!`;
    
      return (
        <div className="w-full max-w-xl mx-auto my-8 px-4">
          <div className="w-full">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 text-gray-800">AI Risk Assessment for: <span className="block sm:inline text-blue-600 mt-1 sm:mt-0">{results.profession}</span></h2>
            {[ { period: '1_year', label: '1 Year Outlook' }, { period: '5_year', label: '5 Year Outlook' }, { period: '10_year', label: '10 Year Outlook' } ].map(({ period, label }) => {
              const score = results.scores[period];
              const periodKey = period.replace('_year', 'y');
              const explanation = narratedText[periodKey] || results.explanations[periodKey];
              const isRefining = !narratedText[periodKey] && narrating[periodKey];
              const colorBase = getRiskColor(score);
              const circleBgClass = `bg-${colorBase}-100`, circleTextClass = `text-${colorBase}-700`, circleBorderClass = `border-${colorBase}-500`, cardLeftBorderClass = `border-l-${colorBase}-500`;

              const isExpanded = !!expandedExplanations[period];
              let displayExplanation = explanation || "No explanation provided.";
              let firstSentence = "";
              let canBeTruncated = false;

              if (explanation) {
                  const sentenceEndMatch = explanation.match(/^([^.!?]+[.!?])(\s|$)/);
                  if (sentenceEndMatch && sentenceEndMatch[1]) {
                      firstSentence = sentenceEndMatch[1];
                      if (firstSentence.length < explanation.length - 10) { // Only consider truncatable if significantly shorter
                          canBeTruncated = true;
                      }
                  } else {
                      firstSentence = explanation; // If no clear sentence or very short, use full
                  }
              }

              if (!isExpanded && canBeTruncated) {
                  displayExplanation = firstSentence;
              }

              return (
                <div key={period} className={`result-card bg-white rounded-xl shadow-lg p-6 mb-6 border-l-4 ${cardLeftBorderClass} transition-all hover:shadow-2xl`}>
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                    <div className={`score-circle ${circleBgClass} ${circleTextClass} h-20 w-20 sm:h-24 sm:w-24 rounded-full flex items-center justify-center text-3xl font-bold flex-shrink-0 border-2 ${circleBorderClass}`}>
                      {typeof score === 'number' ? `${score}%` : '?'}
                    </div>
                    <div className="result-content text-center sm:text-left flex-grow">
                      <h3 className="text-xl font-semibold mb-2 text-gray-700">{label}</h3>
                      {isRefining && (
                        <p className="text-xs text-sky-500 italic mb-1 flex items-center gap-1.5">
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-400 animate-pulse"></span>
                          Refining explanation...
                        </p>
                      )}
                      <p key={narratedText[periodKey] ? 'narrated' : 'template'} className="text-gray-600 text-sm leading-relaxed narrate-fade-in">
                        {displayExplanation}
                        {canBeTruncated && (
                          <button
                            onClick={() => toggleExplanation(period)}
                            className="text-blue-600 hover:text-blue-700 font-semibold ml-1 text-xs underline"
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? 'Read Less' : 'Read More...'}
                          </button>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            {results.suggestions && results.suggestions.length > 0 && (
              <div className="suggestions-card bg-white rounded-xl shadow-lg p-6 mb-6 border-l-4 border-blue-500 transition-all hover:shadow-2xl">
                <h3 className="text-xl font-semibold mb-5 text-gray-700">AI-Resistant Career Path Suggestions</h3>
                <div className="suggestions-list space-y-5">
                  {results.suggestions.map((suggestion, i) => (
                    <div key={i} className="suggestion-item flex items-start gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="suggestion-number bg-blue-500 text-white rounded-full h-8 w-8 flex items-center justify-center text-base font-semibold flex-shrink-0 mt-0.5">{i + 1}</div>
                      <p className="text-gray-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: suggestion }}></p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="reset-button-wrapper text-center my-10">
              <button onClick={startNewAssessment} className="reset-button text-blue-600 hover:text-blue-700 font-medium py-3 px-6 rounded-full transition-colors hover:bg-blue-100 text-base border border-blue-600 hover:border-blue-700">← Clear & Start New Assessment</button>
            </div>

            <div className="mt-10 pt-6 border-t border-gray-200">
              <h4 className="text-lg font-semibold text-center text-gray-700 mb-4 flex items-center justify-center">
                <Share2 size={20} className="mr-2 text-gray-600"/> Share These Results
              </h4>
              <div className="flex justify-center items-center space-x-3 sm:space-x-4">
                <TwitterShareButton url={shareUrl} title={generalShareMessage} hashtags={hashtags}><TwitterIcon size={36} round /></TwitterShareButton>
                <FacebookShareButton url={shareUrl} quote={facebookShareQuote} hashtag={`#${hashtags[0]}`}><FacebookIcon size={36} round /></FacebookShareButton>
                <LinkedinShareButton url={shareUrl}><LinkedinIcon size={36} round /></LinkedinShareButton>
                <WhatsappShareButton url={shareUrl} title={generalShareMessage} separator=" "><WhatsappIcon size={36} round /></WhatsappShareButton>
                <button onClick={() => handleCopyLink(generalShareMessage)} title="Copy summary for sharing" className="p-2 rounded-full hover:bg-gray-200 active:bg-gray-300 transition-colors flex items-center justify-center" aria-label="Copy summary for sharing">
                  <Copy size={20} className="text-gray-600" />
                </button>
              </div>
              {linkCopied && (<p className="text-sm text-green-600 text-center mt-3 transition-opacity duration-300 ease-in-out opacity-100">Message copied to clipboard!</p>)}
            </div>

          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-sky-100 font-sans">
      <div className="flex-grow container mx-auto px-4 max-w-3xl flex flex-col py-8 sm:py-12">
        {config.showAds && <AdComponent position="header" />}
        {!showAbout && (
          <div className="search-section w-full flex flex-col items-center mb-10 text-center">
            <h1 className="text-3xl sm:text-4xl font-extrabold mb-3 text-slate-800">Will AI Take My Job?</h1>
            <p className="text-slate-600 mb-6 text-base sm:text-lg max-w-xl mx-auto">Enter your profession for an AI-powered risk assessment.</p>
            <form onSubmit={handleSubmit} className="w-full max-w-lg space-y-5">
              <div>
                <label htmlFor="profession-input" className="sr-only">Enter your occupation or profession</label>
                <input id="profession-input" type="text" value={profession} onChange={(e) => setProfession(e.target.value)} placeholder="E.g., Software Engineer, Teacher" className="w-full px-6 py-4 rounded-full border-2 border-slate-300 focus:outline-none focus:ring-3 focus:ring-sky-400 focus:border-sky-500 shadow-sm transition-all hover:shadow-md text-base placeholder-slate-400" disabled={loading} aria-label="Profession Input" />
              </div>
              <div className="button-wrapper flex justify-center">
                <button type="submit" disabled={loading || !profession.trim()} className="submit-button bg-sky-600 text-white px-10 py-3.5 rounded-full hover:bg-sky-700 focus:outline-none focus:ring-3 focus:ring-sky-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 text-lg font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95">
                  {loading ? (<><Loader2 className="animate-spin h-6 w-6"/><span>Analyzing...</span></>) : 'Assess My Risk'}
                </button>
              </div>
            </form>
            {error && !loading && (<div role="alert" className="error-message text-red-700 text-center p-4 bg-red-100 border border-red-400 rounded-lg mt-8 w-full max-w-lg shadow"><strong>Error:</strong> {error}</div>)}
            {!results && !loading && !error && config.showWordCloud && <WordCloud onWordClick={word => setProfession(word)} />}
          </div>
        )}
        {renderResultsContent()}
        {config.showAds && <AdComponent position="footer" />}
      </div>
      <ContactInfo onShowAbout={() => { setShowAbout(true); setResults(null); setError(null); setLinkCopied(false);}} />
    </div>
  );
};
export default App;